import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import {
  clearSelectedTemplateId,
  setIsDeletingTemplate,
  setSelectedProjectShortName,
  setSelectedTemplateId,
} from '../store/userSlice';
import { useSelector, useStore } from 'react-redux';
import { useGetTemplatesQuery, useGetUserProjectsQuery } from '../apis/apiApplication';
import { useSelectedProjectShortName } from '../hooks/useSelectedProjectShortName';
import { useSelectedTemplateId } from '../hooks/useSelectedTemplateId';
import { skipToken } from '@reduxjs/toolkit/query';
import { getLastUsedProjectShortName, getSelectedProjectShortName } from '../apis/apiHeader';

import { signalrClient as hubConnection } from '../signalr/client';
import { HubConnectionState } from '@microsoft/signalr';
import { setSelectedCategory } from '../components/global/CategoryTree/categoryTreeSlice';
import { generatePartialPath } from './routerSlice';
import { ROUTES } from '../components/menu/getMenuLeftItems';
import { ConfigurationSubpage } from '../screens/Configuration/Configuration';
import { addToast, createDebugLog } from '@ydistri/utils';
import { ReduxState, resetAllSlices, useAppDispatch } from '../store';
import { TemplateResponse } from '@ydistri/api-sdk';
import { Dispatch } from '@reduxjs/toolkit';
import { setSignalrConnected } from '../store/appSlice';
import LoadingFullPage from '../components/global/LoadingFullPage/LoadingFullPage';
import MessageFullPage from '../components/global/LoadingFullPage/MessageFullPage';
import { useLogout } from '../hooks/useLogout';
import { tokenIdentity } from '../auth/tokenIdentity';
import Auth from '../auth/Auth';
import { useUser } from '../hooks/useUser';
import { problemMessagesOf } from '../screens/Configuration/Validator/problemsLib';
import { Button, Text } from '@ydistri/ds';
import { getSelectedTemplateForProject } from '../store/localStore';
import { isValidTemplateId } from '../lib/templates/templatesLib';
import { ROOT_CATEGORY_ID } from '../components/global/_constants';

const debugLog = createDebugLog('ProjectRoute');

const resetProjectState = (
  dispatch: Dispatch,
  projectShortName: string,
  templateIdFromUrl: string | undefined,
): void => {
  resetAllSlices();
  dispatch(
    setSelectedTemplateId(
      templateIdFromUrl
        ? parseInt(templateIdFromUrl)
        : getSelectedTemplateForProject(projectShortName),
    ),
  );
};

const ProjectRoute: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const { projectShortName, templateId, slug } = useParams();
  const appStatus = useSelector((state: ReduxState) => state.appReducer.status);

  const selectedProjectShortName = useSelectedProjectShortName();
  const selectedTemplateId = useSelectedTemplateId();

  const apiProjectShortName = getSelectedProjectShortName();

  //in case of user login, it can already be initialized => sometimes, that resulted in "Loading.." screen
  const [connectionInitialized, setConnectionInitialized] = useState<boolean>(
    hubConnection.state !== HubConnectionState.Disconnected,
  );
  const {
    data: projects,
    isLoading: isProjectsLoading,
    error: projectsError,
  } = useGetUserProjectsQuery();
  const logout = useLogout();
  const user = useUser();

  // Until the URL's project is selected, the templates on hand belong to another project
  const { currentData: templates, isLoading: isTemplatesLoading } = useGetTemplatesQuery(
    selectedProjectShortName &&
      selectedProjectShortName === projectShortName &&
      apiProjectShortName === selectedProjectShortName
      ? selectedProjectShortName
      : skipToken,
  );

  const generatePartialPathWithTemplateId = useCallback(
    (templateId: number) =>
      generatePartialPath(
        ROUTES.configurationChildRoute,
        { projectShortName, slug: `${ROOT_CATEGORY_ID}`, templateId: templateId.toString() },
        ConfigurationSubpage.CATEGORIES,
      ),
    [projectShortName],
  );

  const setCorrectTemplate = useCallback(
    (
      templates: TemplateResponse[],
      templateIdUrl: string | undefined,
      templateIdStore: number | undefined,
      dispatch: Dispatch,
    ): void => {
      debugLog('======================== template change stuff =====================');
      debugLog('Templates: ', templates);
      const selectedTemplateId = templateIdStore;
      debugLog('templateIdFromUrl', templateIdUrl, 'selectedTemplateId', selectedTemplateId);
      if (templates.length > 0) {
        if (templateIdUrl) {
          //Template id is in url, so it has priority over the one in the store
          debugLog('Template id is in url, so it has priority over the one in the store');
          const templateIdFromUrl = parseInt(templateIdUrl);
          const template = templates.find(t => t.id === templateIdFromUrl);
          if (template) {
            if (templateIdFromUrl !== selectedTemplateId && isValidTemplateId(selectedTemplateId)) {
              debugLog(
                `Template ${templateIdFromUrl} exists and is different from the one in the store (${selectedTemplateId})`,
              );
              dispatch(setSelectedTemplateId(templateIdFromUrl));
            }
          } else {
            //we have wrong template id in the store!
            // => we select the first one and redirect (because the url is wrong)
            debugLog(
              `Template ${templateIdFromUrl} not found, setting to first and redirecting => probably deleted!`,
            );
            const newSelectedTemplateId = templates[0].id;

            dispatch(setSelectedTemplateId(newSelectedTemplateId));

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know that store is ReduxState
            const isDeletingTemplate = (store.getState() as ReduxState).userReducer
              .isDeletingTemplate;

            if (!isDeletingTemplate) {
              dispatch(
                addToast({
                  message: 'Template you had opened was probably deleted. Template was changed!',
                  isError: true,
                }),
              );
              dispatch(setIsDeletingTemplate(undefined));
            }

            const newUrl = generatePartialPathWithTemplateId(newSelectedTemplateId);
            debugLog(`Redirecting to ${newUrl} because template id in url was not valid`);
            navigate(newUrl);
          }
        } else if (isValidTemplateId(selectedTemplateId)) {
          debugLog('Template id is not in URL, but it is in the store');
          const template = templates.find(t => t.id === selectedTemplateId);

          //if found, everything is ok
          //if not found, we select the first one, but we don't redirect
          if (!template) dispatch(setSelectedTemplateId(templates[0].id));
        } else {
          //template id is not in URL, and it is not in the store
          //we select the first one, but we don't redirect
          debugLog(
            "Template id is not in URL, and it is not in the store - we select the first one, but we don't redirect",
          );
          dispatch(setSelectedTemplateId(templates[0].id));
        }
      } else {
        debugLog('Unsetting template id!');
        //        dispatch(setSelectedTemplateId(undefined));
        dispatch(clearSelectedTemplateId());
      }
      debugLog('=========================== template change stuff end =====================');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't need to add all dependencies
    [generatePartialPathWithTemplateId],
  );

  useEffect(() => {
    if (slug) {
      dispatch(setSelectedCategory(slug));
    }
  }, [dispatch, slug]);

  // useEffect(() => {
  //   if (selectedTemplateId) {
  //     debugLog(`selectedTemplateId changed! Setting Template-Id header to `, selectedTemplateId);
  //     API.instance.defaults.headers.common['Template-Id'] = selectedTemplateId;
  //    dispatch(
  //       apiSlice.util.invalidateTags(
  //         getAllTagsExcept([
  //           APITAGS.application.projects,
  //           APITAGS.application.templates,
  //           APITAGS.calculations.calculations,
  //         ]),
  //       ),
  //     );
  //   }
  // }, [dispatch, selectedTemplateId]);

  useEffect(() => {
    // Wait for projects to load
    if (isProjectsLoading || !projects) {
      return;
    }

    debugLog('++++++++++++++++++++++++++++');

    // Handle empty projects list
    if (projects.length === 0) {
      debugLog('== ! projects');
      dispatch(setSelectedProjectShortName(undefined));
      debugLog('-----------------------------');
      return;
    }

    // Case 1: URL has project short name (highest priority - source of truth)
    if (projectShortName) {
      debugLog('Project short name is in the url, so it has priority over the one in the store');
      const foundProject = projects.find(t => t.shortName === projectShortName);

      if (foundProject) {
        // Project in URL exists - sync store to match URL
        if (projectShortName !== selectedProjectShortName) {
          debugLog('Project exists and is different from the one in the store');
          dispatch(setSelectedProjectShortName(projectShortName));
          resetProjectState(dispatch, projectShortName, templateId);
        }
        // No navigation - URL already correct
        debugLog('-----------------------------');
        return;
      }

      // Project in URL doesn't exist - redirect to first available project
      debugLog('Project in URL not found, redirecting to first available project');
      navigate(`/${projects[0].shortName}/calculations`);
      debugLog('-----------------------------');
      return;
    }

    // Case 2: No URL short name, but a project was used before
    const lastUsedProjectShortName = selectedProjectShortName ?? getLastUsedProjectShortName();
    if (lastUsedProjectShortName) {
      debugLog('Project short name is not in URL, but a project was used before');
      const foundProject = projects.find(t => t.shortName === lastUsedProjectShortName);

      if (foundProject) {
        // Store project exists - add it to URL
        debugLog('Store project found, navigating to add it to URL');
        navigate(`/${foundProject.shortName}/calculations`);
      } else {
        // Store project doesn't exist - use first project
        debugLog('Store project not found, navigating to first available project');
        navigate(`/${projects[0].shortName}/calculations`);
      }
      debugLog('-----------------------------');
      return;
    }

    // Case 3: Neither URL nor store has project - use first available
    debugLog('No project in URL or store, navigating to first available project');
    navigate(`/${projects[0].shortName}/calculations`);
    debugLog('-----------------------------');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't need to add all dependencies
  }, [projectShortName, selectedProjectShortName, projects, isProjectsLoading, dispatch]); //"navigate" is not needed here

  useEffect(() => {
    // Before the URL's project is selected, the template would be saved under another project
    if (projectShortName && projectShortName === selectedProjectShortName) {
      resetProjectState(dispatch, projectShortName, templateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the URL's project changes
  }, [dispatch, projectShortName]);

  useEffect(() => {
    if (!isTemplatesLoading && templates) {
      setCorrectTemplate(templates, templateId, selectedTemplateId, dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't need to add all dependencies
  }, [templates, templates?.length, isTemplatesLoading, templateId, selectedTemplateId, dispatch]); //"navigate" is not needed here

  useEffect(() => {
    (async function connect() {
      if (hubConnection.state === HubConnectionState.Disconnected) {
        debugLog('SignalR: hubConnection.state === HubConnectionState.Disconnected');

        await hubConnection
          .start()
          .then(() => {
            debugLog(`SignalR: ConnectionId: ${hubConnection.connectionId ?? ''}`);
            setConnectionInitialized(true);
          })
          .catch(debugLog);
      }
    })();
  }, [selectedProjectShortName]);

  useEffect(() => {
    if (selectedProjectShortName && connectionInitialized) {
      debugLog(
        'SignalR: selectedProjectShortName changed => Invoking connection... ',
        selectedProjectShortName,
      );
      hubConnection
        .invoke('JoinProject', selectedProjectShortName)
        .then(() => {
          debugLog('SignalR: JoinProject successful!');
          dispatch(setSignalrConnected(true));
        })
        .catch((err: unknown) => {
          debugLog(err);
          dispatch(setSignalrConnected(false));
        });
    }
  }, [selectedProjectShortName, connectionInitialized, dispatch]);

  if (!appStatus) return <LoadingFullPage text="Loading." />;
  if (!connectionInitialized) return <LoadingFullPage text="Loading.." />;

  // Projects finished loading but there is nothing to open: the account has no project, or the
  // request failed (a token another identity issued answers 401 here). Either way the screen says
  // who is signed in and lets them leave; without this the app would spin on "Loading..." forever,
  // or show "no project" for what is an authentication failure. An unknown slug in the URL does NOT
  // land here - the resolution effect above redirects it to the first available project.
  if (!isProjectsLoading && (!projects || projects.length === 0)) {
    // A password-grant token names only the user id; the Microsoft exchange adds name and email.
    const identity = tokenIdentity(new Auth().getBearer());
    const who =
      user?.fullName ??
      identity.name ??
      (identity.subject ? `user ${identity.subject}` : undefined);
    const login = user?.email ?? identity.email;
    return (
      <MessageFullPage
        text={
          projectsError
            ? 'Your projects could not be loaded.'
            : 'No project is available for your account.'
        }
      >
        {projectsError && (
          <Text $type="danger">{problemMessagesOf(projectsError, 'The request failed')}</Text>
        )}
        <Text>
          Signed in as {who ?? 'an unknown user'}
          {login ? ` (${login})` : ''}
        </Text>
        <Button
          onClick={() => {
            void logout();
          }}
        >
          Log out
        </Button>
      </MessageFullPage>
    );
  }

  // A screen rendered for another project would read and write with that project's header
  const isUrlProjectSelected =
    !projectShortName ||
    (selectedProjectShortName === projectShortName && apiProjectShortName === projectShortName);
  if (!apiProjectShortName || !isUrlProjectSelected) return <LoadingFullPage text="Loading..." />;

  return <Outlet />;
};

export default ProjectRoute;
